import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";
import { ApiError, errorCode, errorMessage, errorTraceId } from "./errors";
import type { components } from "./schema";

export type DashboardSummary = components["schemas"]["DashboardSummary"];
export type PaginationMeta = components["schemas"]["PaginationMeta"];

/** Resposta pronta de `/dashboard/appointments/upcoming`: lista + paginação. */
export interface UpcomingAppointmentsPage {
  appointments: components["schemas"]["Appointment"][];
  meta: PaginationMeta;
}

/**
 * Toda query passa por aqui: openapi-fetch devolve `{ data, error }` em vez de
 * lançar, mas o TanStack Query precisa de uma exceção pra marcar a query como
 * falha. Este adaptador faz a ponte sem perder o `code`.
 */
export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown }>,
): Promise<T> {
  const { data, error } = await request;
  if (error || data === undefined) {
    throw new ApiError(
      errorCode(error) ?? "unknown_error",
      errorMessage(error),
      errorTraceId(error),
    );
  }
  return data;
}

/**
 * Para as rotas que respondem **204 sem corpo** — marcar lido, desconectar
 * número, desinscrever push.
 *
 * O `unwrap` acima não serve nelas: ele trata `data === undefined` como
 * falha, e num 204 é exatamente isso que chega. O resultado era uma
 * operação que dava certo no servidor e aparecia como erro na tela — foi o
 * que fazia "Desconectar número" desconectar de verdade e ainda assim
 * mostrar "não foi possível concluir agora".
 *
 * Aqui só o `error` decide.
 */
export async function unwrapNoContent(
  request: Promise<{ error?: unknown }>,
): Promise<void> {
  const { error } = await request;
  if (error) {
    throw new ApiError(
      errorCode(error) ?? "unknown_error",
      errorMessage(error),
      errorTraceId(error),
    );
  }
}

export function dashboardSummaryQuery() {
  return queryOptions({
    queryKey: ["dashboard", "summary"] as const,
    queryFn: async () => {
      const body = await unwrap(api.GET("/dashboard/summary"));
      if (!body.data) {
        throw new ApiError("malformed_response", "A resposta veio sem `data`.");
      }
      return body.data;
    },
  });
}

/** Tamanho fixo da página de próximos agendamentos do dashboard. */
const UPCOMING_PAGE_SIZE = 10;

/**
 * Próximos agendamentos (paginado, `meta.total`). Diferente de
 * `GET /appointments`, a API já exclui cancelados e ordena por `starts_at`
 * ascendente — a tela não precisa filtrar nem ordenar nada.
 */
export function upcomingAppointmentsQuery(page: number) {
  return queryOptions({
    queryKey: ["dashboard", "upcoming-appointments", page] as const,
    queryFn: async (): Promise<UpcomingAppointmentsPage> => {
      const body = await unwrap(
        api.GET("/dashboard/appointments/upcoming", {
          params: { query: { page, limit: UPCOMING_PAGE_SIZE } },
        }),
      );
      if (!body.data || !body.meta) {
        throw new ApiError("malformed_response", "A resposta veio sem `data`/`meta`.");
      }
      return { appointments: body.data, meta: body.meta };
    },
  });
}

export function meQuery() {
  return queryOptions({
    queryKey: ["auth", "me"] as const,
    queryFn: () => unwrap(api.GET("/auth/me")),
  });
}
